import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";
import { Activity, Loader2, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

export default function AVEDAPIPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/pi-config`, { headers: getAuthHeaders() });
      setConfig(resp.data);
    } catch { toast.error("Failed to load PI config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pi-config`, config, { headers: getAuthHeaders() });
      toast.success("PI System configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const fetchSnapshot = async () => {
    try {
      const resp = await axios.get(`${API}/pi/snapshot`, { headers: getAuthHeaders() });
      setSnapshot(resp.data.tags || []);
    } catch { toast.error("Failed to fetch tag data"); }
  };

  const searchTags = async () => {
    if (!tagSearch) return;
    try {
      const resp = await axios.get(`${API}/pi/tags?search=${encodeURIComponent(tagSearch)}`, { headers: getAuthHeaders() });
      setSearchResults(resp.data.items || []);
    } catch { toast.error("Tag search failed"); }
  };

  const addTag = (tag) => {
    const tags = config.monitored_tags || [];
    if (tags.find(t => t.web_id === tag.web_id)) return;
    setConfig({ ...config, monitored_tags: [...tags, tag] });
  };

  const removeTag = (webId) => {
    setConfig({ ...config, monitored_tags: (config.monitored_tags || []).filter(t => t.web_id !== webId) });
  };

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  if (loading || !config) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64 text-[#64748B] font-mono">Loading PI System configuration...</div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="pi-config-page">
        <PageHeader title="AVEVA PI System" subtitle="Connect to PI Web API for real-time machine health monitoring" testId="pi-title">
          <Button onClick={handleSave} disabled={saving} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="pi-save-btn">{saving ? "Saving..." : "Save Configuration"}</Button>
        </PageHeader>

        <IntegrationHealthBadge integration="pi" onTestConnection={async () => { const r = await axios.post(`${API}/admin/pi-test`, {}, { headers: getAuthHeaders() }); return r.data; }} />

        <div className="bg-white border border-[#E2E8F0] p-6">
          <h3 className="font-bold text-sm text-[#0F172A] mb-4 flex items-center gap-2"><Activity size={16} /> Connection</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Label className="w-24">Enabled</Label>
              <button onClick={() => update("enabled", !config.enabled)} className={`w-12 h-6 rounded-full transition-colors ${config.enabled ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} data-testid="pi-enabled-toggle">
                <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${config.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>
            <div>
              <Label>PI Web API URL *</Label>
              <Input value={config.server_url || ""} onChange={e => update("server_url", e.target.value)} className="mt-1 rounded-none font-mono text-sm" placeholder="https://pi-server.company.com" data-testid="pi-server-url" />
            </div>
            <div>
              <Label>Auth Type</Label>
              <select value={config.auth_type || "basic"} onChange={e => update("auth_type", e.target.value)} className="mt-1 w-full h-10 px-3 border border-[#E2E8F0] bg-white text-sm" data-testid="pi-auth-type">
                <option value="basic">Basic (Username/Password)</option>
                <option value="bearer">Bearer Token (OIDC via AIM)</option>
                <option value="kerberos">Kerberos (Windows AD)</option>
              </select>
            </div>
            {config.auth_type === "basic" && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Username</Label><Input value={config.username || ""} onChange={e => update("username", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="pi-username" /></div>
                <div><Label>Password</Label><Input type="password" value={config.password || ""} onChange={e => update("password", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="pi-password" /></div>
              </div>
            )}
            {config.auth_type === "bearer" && (
              <div><Label>Bearer Token</Label><Input type="password" value={config.bearer_token || ""} onChange={e => update("bearer_token", e.target.value)} className="mt-1 rounded-none font-mono text-sm" data-testid="pi-bearer-token" /></div>
            )}
            <div><Label>Poll Interval (minutes)</Label><Input type="number" value={config.poll_interval_minutes || 15} onChange={e => update("poll_interval_minutes", parseInt(e.target.value) || 15)} className="mt-1 rounded-none text-sm w-32" data-testid="pi-poll-interval" /></div>
          </div>
        </div>

        {/* Tag Search */}
        <div className="bg-white border border-[#E2E8F0] p-6">
          <h3 className="font-bold text-sm text-[#0F172A] mb-4">Search & Monitor PI Tags</h3>
          <div className="flex gap-2 mb-4">
            <Input value={tagSearch} onChange={e => setTagSearch(e.target.value)} placeholder="Search PI tags (e.g., Temp*, Pressure*)" className="rounded-none text-sm flex-1" data-testid="pi-tag-search" />
            <Button onClick={searchTags} variant="outline" className="rounded-none" data-testid="pi-tag-search-btn">Search</Button>
          </div>
          {searchResults.length > 0 && (
            <div className="border border-[#E2E8F0] max-h-48 overflow-y-auto mb-4">
              {searchResults.map(t => (
                <div key={t.web_id} className="flex items-center justify-between px-3 py-2 border-b border-[#F1F5F9] text-sm hover:bg-[#FAFAFA]">
                  <span className="font-mono text-[#0F172A]">{t.name}</span>
                  <Button size="sm" onClick={() => addTag(t)} className="rounded-none bg-[#10B981] text-white text-xs" data-testid={`add-tag-${t.name}`}><Plus size={12} className="mr-1" />Add</Button>
                </div>
              ))}
            </div>
          )}
          {(config.monitored_tags || []).length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold text-[#64748B] uppercase">Monitored Tags ({config.monitored_tags.length})</h4>
                <Button size="sm" variant="outline" onClick={fetchSnapshot} className="rounded-none text-xs" data-testid="pi-refresh-snapshot"><RefreshCw size={12} className="mr-1" />Refresh</Button>
              </div>
              <div className="space-y-1">
                {config.monitored_tags.map(t => {
                  const s = snapshot.find(sn => sn.tag_name === t.name);
                  return (
                    <div key={t.web_id} className="flex items-center justify-between p-2 bg-[#FAFAFA] border border-[#E2E8F0]">
                      <span className="font-mono text-sm text-[#0F172A]">{t.name}</span>
                      {s && <span className="font-mono text-sm font-bold" style={{ color: s.good !== false ? "#10B981" : "#DC2626" }}>{s.value} {s.unit}</span>}
                      <button onClick={() => removeTag(t.web_id)} className="text-[#DC2626] hover:text-[#991B1B]"><Trash2 size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
